<script setup lang="ts">
import { computed } from 'vue'

import BaseStatRow from '@/components/ui/BaseStatRow.vue'
import { formatTime } from '@/services/formattingService'
import { shouldHideAscentForTour } from '@/services/tourTypeService'
import {
  calculateTrackDistance,
  getTrackElevationGain,
  getTrackElevationLoss,
  getTrackTimeElapsed,
  getTrackTimeMoving,
} from '@/services/trackMetricsCalculationService'
import type { Tour } from '@/types/tour'

defineOptions({ name: 'RouteStatsRow' })

const props = withDefaults(
  defineProps<{
    tour: Tour
    /**
     * When provided, stats are computed over this track subset instead of every track in the
     * tour — used by the Route sheet so the headline numbers match the profiled track shown in
     * the elevation chart (a multi-track tour would otherwise total all stages).
     */
    tracks?: Tour['tracks']
    /**
     * When set (e.g. an A→B segment selection), show these distance / ascent / descent values
     * instead of the track-derived ones, so the stats follow the chart selection.
     */
    override?: { distanceM: number; gainM: number; lossM: number } | null
    /** Average uphill grade (positive %), shown small next to ascent — MapOut-style. */
    ascentGradePercent?: number | null
    /** Average downhill grade (negative %), shown small next to descent. */
    descentGradePercent?: number | null
    /**
     * `primary` (default): distance + ascent + descent — the universal route headline, kept to
     * a single row. `secondary`: time + avg speed — only meaningful for recorded activities.
     */
    variant?: 'primary' | 'secondary'
  }>(),
  {
    tracks: undefined,
    override: null,
    ascentGradePercent: null,
    descentGradePercent: null,
    variant: 'primary',
  },
)

const statTracks = computed(() => props.tracks ?? props.tour.tracks)

const sumOverTracks = (selector: (track: Tour['tracks'][number]) => number | null | undefined) => {
  let total: number | null = null
  for (const track of statTracks.value) {
    const value = selector(track)
    if (value !== null && value !== undefined) total = (total ?? 0) + value
  }
  return total
}

const distanceM = computed(() => sumOverTracks(calculateTrackDistance) ?? 0)
const elevationGain = computed(() => sumOverTracks(getTrackElevationGain))
const elevationLoss = computed(() => sumOverTracks(getTrackElevationLoss))
const hideAscent = computed(() => shouldHideAscentForTour(props.tour, statTracks.value))

// The tour-level totals only describe the whole tour; with a track subset we sum the subset
// so time/speed stay consistent with the profiled distance.
const usesTrackSubset = computed(() => props.tracks !== undefined)

const timeElapsed = computed(() => {
  if (
    !usesTrackSubset.value &&
    props.tour.time_elapsed !== null &&
    props.tour.time_elapsed !== undefined
  ) {
    return props.tour.time_elapsed
  }
  return sumOverTracks(getTrackTimeElapsed)
})

const timeMoving = computed(() => {
  if (
    !usesTrackSubset.value &&
    props.tour.time_moving !== null &&
    props.tour.time_moving !== undefined
  ) {
    return props.tour.time_moving
  }
  return sumOverTracks(getTrackTimeMoving)
})

const averageSpeedKmh = computed(() => {
  const moving = timeMoving.value
  if (!moving || moving === 0 || distanceM.value === 0) return null
  return distanceM.value / 1000 / (moving / 3600)
})

const formatDistance = (meters: number) => `${(meters / 1000).toFixed(1)} km`
const formatGrade = (grade: number | null | undefined) => {
  if (grade === null || grade === undefined) return null
  const rounded = Math.round(grade)
  // Don't show a noisy "−0%" / "+0%" for near-flat sections.
  if (rounded === 0) return null
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)}%`
}

type Stat = { key: string; value: string; label: string; grade?: string | null }

const stats = computed(() => {
  // Secondary: activity-only moving time + avg speed (time lives in the header now).
  if (props.variant === 'secondary') {
    if (props.override) return []
    const items: Stat[] = []
    if (timeMoving.value !== null) {
      items.push({ key: 'moving', value: formatTime(timeMoving.value), label: 'Moving time' })
    }
    if (averageSpeedKmh.value !== null) {
      items.push({
        key: 'speed',
        value: `${averageSpeedKmh.value.toFixed(1)} km/h`,
        label: 'Avg speed',
      })
    }
    return items
  }

  // Primary: distance + ascent + descent, with inline up/down grades. One row.
  const ascentGrade = formatGrade(props.ascentGradePercent)
  const descentGrade = formatGrade(props.descentGradePercent)
  const items: Stat[] = []

  if (props.override) {
    items.push({
      key: 'distance',
      value: formatDistance(props.override.distanceM),
      label: 'Distance',
    })
    if (!hideAscent.value) {
      items.push({
        key: 'gain',
        value: `+${Math.round(props.override.gainM)} m`,
        label: 'Ascent',
        grade: ascentGrade,
      })
    }
    items.push({
      key: 'loss',
      value: `−${Math.round(props.override.lossM)} m`,
      label: 'Descent',
      grade: descentGrade,
    })
    return items
  }

  // Fixed column order Distance · Time · Ascent · Descent — the canonical touring headline (matches
  // Komoot and the collection sheet's Distance · Time · Ascent · Avg). Routes (no timestamps) drop
  // Time → Distance · Ascent · Descent; an A→B segment (no time either) shows the same three.
  if (distanceM.value > 0) {
    items.push({ key: 'distance', value: formatDistance(distanceM.value), label: 'Distance' })
  }
  if (timeElapsed.value !== null) {
    items.push({ key: 'time', value: formatTime(timeElapsed.value), label: 'Time' })
  }
  if (!hideAscent.value && elevationGain.value !== null) {
    items.push({
      key: 'gain',
      value: `+${Math.round(elevationGain.value)} m`,
      label: 'Ascent',
      grade: ascentGrade,
    })
  }
  if (elevationLoss.value !== null) {
    items.push({
      key: 'loss',
      value: `−${Math.round(elevationLoss.value)} m`,
      label: 'Descent',
      grade: descentGrade,
    })
  }
  return items
})

const hasStats = computed(() => stats.value.length > 0)

defineExpose({ hasStats })
</script>

<template>
  <BaseStatRow v-if="hasStats" :stats="stats" data-testid="route-stats-row" />
</template>
